import { useState, useEffect, useCallback } from "react";
import {
  logoffOperatorOfCrane,
  getCraneOfOperator,
} from "../../repository/BilletYardInventoryRepository";
import {
  getStoredCraneId,
  getStoredOperatorId,
  hasValidStoredIds,
} from "../../utils";
import { useProfiles } from "../core/useProfiles";
import { BilletYardOperator, ProfileCapabilities } from "../../models";

interface UseAuthenticationFlowProps {
  onOpenCraneSelectionModal: () => void;
  onOpenLocationPickerModal: () => void;
  onClearCraneSelection?: () => void;
}

interface UseAuthenticationFlowReturn {
  // Permission state
  permissionAlert: {
    open: boolean;
    message: string;
  };

  // Authentication state
  selectedCraneId: number | null;
  selectedOperatorId: number | null;
  isLoggedIn: boolean;

  // Touch authentication state
  showTouchKeypad: boolean;
  touchAuthenticationPassed: boolean;
  hasUserInteracted: boolean;

  // Profile capabilities
  canOpenMoveBilletsModal: boolean;
  capabilities: ProfileCapabilities;
  isSupervisor: boolean;
  canAccessLocationContentEdition: boolean;
  isTouchMode: boolean;
  shouldBypassCraneSelection: boolean;
  canManageCranes: boolean;
  showLogoutButton: boolean;

  // Handler functions
  handleLocationEditionClick: () => void;
  handleClosePermissionAlert: () => void;
  handleCraneSelectionClick: () => void;
  handleLogoutClick: () => Promise<void>;
  handleTouchLoginSuccess: (operatorCode: string) => void;
  handleTouchKeypadClose: () => void;
  handleLoginButtonClick: () => void;
  handleOperatorSelect: (operator: BilletYardOperator | null) => void;
  handleCraneSelect: (craneId: number | null) => void;
}

export const useAuthenticationFlow = ({
  onOpenCraneSelectionModal,
  onOpenLocationPickerModal,
  onClearCraneSelection,
}: UseAuthenticationFlowProps): UseAuthenticationFlowReturn => {
  // Permission notification state
  const [permissionAlert, setPermissionAlert] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  // Simple crane selection state
  const [selectedCraneId, setSelectedCraneId] = useState<number | null>(null);

  // Simple operator selection state
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(
    null,
  );

  // Touch authentication state - show keypad for Touch Access users who aren't logged in
  const [showTouchKeypad, setShowTouchKeypad] = useState(false);
  const [touchAuthenticationPassed, setTouchAuthenticationPassed] =
    useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // BYI Role and access mode permissions
  const {
    canOpenMoveBilletsModal,
    capabilities,
    isSupervisor,
    canAccessLocationContentEdition,
    // Access mode capabilities
    isTouchMode,
    shouldBypassCraneSelection,
    canManageCranes,
    showLogoutButton,
  } = useProfiles();

  // Use the utility function from utils

  // Login state - user is logged in if:
  // 1. They bypass crane selection (MES users/supervisors) with system defaults, OR
  // 2. They have valid crane and operator selected (excluding "Unknown" crane)
  const isLoggedIn = shouldBypassCraneSelection
    ? selectedCraneId !== null && selectedOperatorId !== null // Accept -1 for bypass users
    : selectedCraneId !== null &&
      selectedCraneId !== 0 &&
      selectedCraneId !== -1 &&
      selectedOperatorId !== null &&
      selectedOperatorId !== -1;

  // Check if touch keypad should be shown on initial load (only once)
  useEffect(() => {
    // Only show keypad if we don't have valid stored IDs
    if (
      isTouchMode &&
      !touchAuthenticationPassed &&
      !isLoggedIn &&
      !shouldBypassCraneSelection &&
      !hasUserInteracted &&
      !hasValidStoredIds(shouldBypassCraneSelection)
    ) {
      // Skip keypad for operators and go directly to crane selection
      if (capabilities.isOperator && !isSupervisor) {
        setTouchAuthenticationPassed(true);
        setHasUserInteracted(true);
        onOpenCraneSelectionModal();
      } else {
        setShowTouchKeypad(true);
      }
    }
  }, [
    isTouchMode,
    touchAuthenticationPassed,
    isLoggedIn,
    shouldBypassCraneSelection,
    hasUserInteracted,
    capabilities.isOperator,
    isSupervisor,
    onOpenCraneSelectionModal,
  ]);

  // Sync localStorage values on every refresh and component mount
  useEffect(() => {
    const syncLocalStorageValues = () => {
      // Bypass crane selection logic - use fixed IDs like old system
      if (shouldBypassCraneSelection) {
        // MES mode users or supervisors use fixed IDs (-1) and don't need crane/operator selection
        const supervisorCraneId = -1;
        const supervisorOperatorId = -1;

        // Set supervisor IDs in localStorage for API compatibility
        localStorage.setItem("selectedCraneId", supervisorCraneId.toString());
        localStorage.setItem(
          "selectedOperatorId",
          supervisorOperatorId.toString(),
        );

        // Update state to reflect supervisor IDs
        setSelectedCraneId(supervisorCraneId);
        setSelectedOperatorId(supervisorOperatorId);
        return;
      }

      // Regular operator logic - sync from localStorage
      const currentStoredCraneId = getStoredCraneId();
      const currentStoredOperatorId = getStoredOperatorId();

      // Update crane ID state if localStorage value differs
      if (currentStoredCraneId !== selectedCraneId) {
        setSelectedCraneId(currentStoredCraneId);
      }

      // Update operator ID state if localStorage value differs
      if (currentStoredOperatorId !== selectedOperatorId) {
        setSelectedOperatorId(currentStoredOperatorId);
      }
    };

    // Sync on component mount
    syncLocalStorageValues();
  }, [shouldBypassCraneSelection, selectedCraneId, selectedOperatorId]);

  // Handler functions with memory leak prevention
  const handleLocationEditionClick = useCallback(() => {
    if (canAccessLocationContentEdition) {
      // Open location picker modal
      onOpenLocationPickerModal();
    } else {
      setPermissionAlert({
        open: true,
        message: "You don't have permission to edit location content.",
      });
    }
  }, [canAccessLocationContentEdition, onOpenLocationPickerModal]);

  const handleClosePermissionAlert = useCallback(() => {
    setPermissionAlert({ open: false, message: "" });
  }, []);

  const handleCraneSelectionClick = useCallback(() => {
    onOpenCraneSelectionModal();
  }, [onOpenCraneSelectionModal]);

  const handleLogoutClick = useCallback(async () => {
    if (isSupervisor) {
      return;
    }

    try {
      // Check if operator still has a crane assigned before calling logoff
      let shouldCallLogoff = false;
      if (
        selectedOperatorId &&
        selectedOperatorId !== -1 &&
        selectedOperatorId !== -2
      ) {
        try {
          // Check if this operator still has a crane assigned
          const craneResponse = await getCraneOfOperator(selectedOperatorId);
          const operatorCraneId =
            craneResponse?.ok && "data" in craneResponse
              ? craneResponse.data?.tables?.[0]?.rows?.[0]?.CraneID
              : null;

          // Only call logoff if operator still has a crane (meaning they're still the current operator)
          // If operatorCraneId is null, another operator has already taken over the crane
          shouldCallLogoff = operatorCraneId !== null;
        } catch (apiError) {
          // If error checking, don't call logoff to be safe
          shouldCallLogoff = false;
        }
      }

      // Only call logoff API if operator still has a crane assigned
      if (
        shouldCallLogoff &&
        selectedCraneId &&
        selectedCraneId !== -1 &&
        selectedCraneId !== -2
      ) {
        try {
          // Call the API to clear the crane's CurrentOperatorID
          await logoffOperatorOfCrane(selectedCraneId, true, true);
        } catch (apiError) {
          // Handle error silently
        }
      }

      // ALWAYS clear localStorage regardless of logoff call
      localStorage.removeItem("selectedCraneId");
      localStorage.removeItem("selectedOperatorId");

      // Clear crane selection from useCranes hook
      if (onClearCraneSelection) {
        onClearCraneSelection();
      }

      // Reset React states
      setSelectedCraneId(null);
      setSelectedOperatorId(null);

      // Reset touch authentication when logging out in Touch mode
      if (isTouchMode) {
        setTouchAuthenticationPassed(false);
        setHasUserInteracted(false);
      }

      // For now, we'll just reset the state and stay on the page
    } catch (error) {
      // Handle error silently
    }
  }, [
    selectedCraneId,
    selectedOperatorId,
    isTouchMode,
    isSupervisor,
    onClearCraneSelection,
  ]);

  const handleTouchLoginSuccess = useCallback(
    (operatorCode: string) => {
      // Touch authentication passed - proceed to crane/operator selection
      setTouchAuthenticationPassed(true);
      setShowTouchKeypad(false);
      setHasUserInteracted(true);

      // For supervisors (code 999), bypass crane selection
      if (operatorCode === "999" && isSupervisor) {
        const supervisorCraneId = -1;
        const supervisorOperatorId = -1;

        localStorage.setItem("selectedCraneId", supervisorCraneId.toString());
        localStorage.setItem(
          "selectedOperatorId",
          supervisorOperatorId.toString(),
        );
        setSelectedCraneId(supervisorCraneId);
        setSelectedOperatorId(supervisorOperatorId);
      } else {
        // For operators, check if we already have valid IDs in localStorage
        if (hasValidStoredIds(shouldBypassCraneSelection)) {
          // IDs are already stored, no need to show crane selection modal
          const storedCraneId = getStoredCraneId();
          const storedOperatorId = getStoredOperatorId();
          setSelectedCraneId(storedCraneId);
          setSelectedOperatorId(storedOperatorId);
        } else {
          // No valid IDs stored, show crane selection modal
          onOpenCraneSelectionModal();
        }
      }
    },
    [isSupervisor, shouldBypassCraneSelection, onOpenCraneSelectionModal],
  );

  const handleTouchKeypadClose = useCallback(() => {
    // Cancel login - user will see the screen without authentication
    setShowTouchKeypad(false);
    setTouchAuthenticationPassed(false); // User canceled, so they are not authenticated
    setHasUserInteracted(true); // Mark that user has interacted, so keypad won't auto-show
    // They will see the Login button instead of Menu button
  }, []);

  const handleLoginButtonClick = useCallback(() => {
    if (isSupervisor) {
      return;
    }

    // Skip keypad for operators and go directly to crane selection
    if (capabilities.isOperator) {
      setTouchAuthenticationPassed(true);
      setHasUserInteracted(true);
      onOpenCraneSelectionModal();
    } else {
      // Show touch keypad for non-operators
      setShowTouchKeypad(true);
    }
  }, [isSupervisor, capabilities.isOperator, onOpenCraneSelectionModal]);

  const handleOperatorSelect = useCallback(
    (operator: BilletYardOperator | null) => {
      setSelectedOperatorId(operator?.OperatorId || null);
    },
    [],
  );

  const handleCraneSelect = useCallback((craneId: number | null) => {
    // Update local state
    setSelectedCraneId(craneId);
    // Update localStorage for persistence
    if (craneId !== null) {
      localStorage.setItem("selectedCraneId", craneId.toString());
    } else {
      localStorage.removeItem("selectedCraneId");
    }
  }, []);

  return {
    // Permission state
    permissionAlert,

    // Authentication state
    selectedCraneId,
    selectedOperatorId,
    isLoggedIn,

    // Touch authentication state
    showTouchKeypad,
    touchAuthenticationPassed,
    hasUserInteracted,

    // Profile capabilities
    canOpenMoveBilletsModal,
    capabilities,
    isSupervisor,
    canAccessLocationContentEdition,
    isTouchMode,
    shouldBypassCraneSelection,
    canManageCranes,
    showLogoutButton,

    // Handler functions
    handleLocationEditionClick,
    handleClosePermissionAlert,
    handleCraneSelectionClick,
    handleLogoutClick,
    handleTouchLoginSuccess,
    handleTouchKeypadClose,
    handleLoginButtonClick,
    handleOperatorSelect,
    handleCraneSelect,
  };
};
